import { useTranslation } from 'next-i18next/pages';
import { useMemo } from 'react';

import useCurrentLang from '@/hooks/apps/useCurrentLang';

import { SAFETY_COMPOSITE_SCENARIO } from '@/data/shared/safetyCompositeScenario';
import { isSafetySoundOffered } from '@/data/shared/safetyCopy';

import CompositeEventTimeline from './CompositeEventTimeline';
import { ExclamationTriangleIcon, XMarkIcon } from './compositeEventIcons';
import { splitRuleName } from './compositeEventLayout';

/**
 * ### CompositeEventPopupMock
 *
 * The monitoring system's composite-event pop-up, rebuilt for the website
 * from the console's own layout and driven by demo data: the warning disc,
 * the rule name, the place, and the timeline of every detection that made
 * up the event. The card keeps the console's light theme so it reads as a
 * screen of the product, not as page copy.
 *
 * The card lays itself out by its own width, not the viewport's, because it
 * shares its frame with the scene photograph: given 672px or more it puts
 * the disc beside the title as the console does, and in less it stacks them,
 * as the console does on a phone.
 *
 * Every clip on the timeline can be played. The close glyph is drawn but
 * inert: there is nothing to close on a page. Detections of a sound not
 * offered in the page language are left off, so the English timeline never
 * shows a rescue call the English sound list does not name; their audio
 * still carries the voice, as a real microphone would.
 *
 * @returns The rendered pop-up element
 * @see {@link SAFETY_COMPOSITE_SCENARIO} The demo event shown here
 */
export default function CompositeEventPopupMock() {
  const { t } = useTranslation('common');
  const lang = useCurrentLang();
  const scenario = SAFETY_COMPOSITE_SCENARIO;
  const events = useMemo(
    () =>
      scenario.events.filter((event) =>
        isSafetySoundOffered(event.label, lang),
      ),
    [scenario.events, lang],
  );
  const ruleTitle = splitRuleName(t(scenario.ruleName));
  const translate = (key: string, values?: Record<string, string | number>) =>
    t(key, values);

  return (
    <div className="@container flex h-full flex-col rounded-[12px] bg-[#f4f6f9] p-4 text-[#14151a] shadow-[0_0_0_2px_rgba(239,68,68,0.45),0_24px_80px_rgba(0,0,0,0.45)] @md:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 @md:gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[rgba(239,68,68,0.5)] text-xs font-bold text-[#b91c1c] @md:h-6 @md:w-6 @md:text-sm">
            !
          </span>
          <span className="text-xs font-medium text-[#991b1b] @2xl:text-base">
            {t('복합 이벤트 감지됨')}
          </span>
        </div>
        <XMarkIcon className="h-5 w-5 text-[#373747] @md:h-6 @md:w-6" />
      </div>

      <div className="mt-4 grid grid-cols-1 items-center gap-4 @md:mt-5 @md:gap-6 @2xl:grid-cols-[200px_1fr] @2xl:items-start @4xl:grid-cols-[260px_1fr]">
        <div className="flex flex-col items-center p-1 @md:p-2">
          <span className="inline-flex h-32 w-32 items-center justify-center rounded-full bg-[linear-gradient(90deg,#6366f1,#a855f7,#ec4899)] text-white @md:h-40 @md:w-40 @2xl:h-44 @2xl:w-44 @4xl:h-60 @4xl:w-60">
            <ExclamationTriangleIcon className="h-24 w-24 p-2 @md:h-28 @md:w-28 @2xl:h-32 @2xl:w-32 @4xl:h-40 @4xl:w-40" />
          </span>
        </div>

        <div className="flex w-full min-w-0 flex-col justify-center">
          <div className="flex w-full min-w-0 flex-col items-center gap-1 @md:gap-2 @2xl:items-start">
            {/*
              The situation stays one line at display size; a qualifier such
              as "폭행 의심" drops to a smaller line under it instead of pushing
              the title into an ellipsis.
            */}
            <p className="w-full text-center font-bold tracking-tight text-[#14151a] @2xl:text-left">
              <span className="block truncate text-[56px] leading-[1.05] @md:text-[72px] @2xl:text-[80px] @4xl:text-[104px]">
                {ruleTitle.main}
              </span>
              {ruleTitle.qualifier && (
                <span className="mt-1 block break-keep text-[22px] leading-[1.3] text-[#373747] @md:mt-2 @md:text-[26px] @4xl:text-[34px]">
                  {ruleTitle.qualifier}
                </span>
              )}
            </p>
            <p className="mt-2 flex flex-wrap items-center justify-center gap-x-1 text-center @2xl:justify-start @2xl:text-left">
              <span className="text-sm font-semibold text-[#373747] @md:text-base @4xl:text-lg">
                {t(scenario.ruleDescription)}
              </span>
              <span className="text-[#696c76]">/</span>
              <span className="text-sm text-[#696c76] @md:text-base @4xl:text-lg">
                {t(scenario.location)} · {t('시연 데이터')}
              </span>
            </p>
          </div>

          <div className="mt-4 rounded-[6px] border border-[#dadfe9] bg-[#f4f6f9] p-3 @md:mt-5 @4xl:mt-6">
            <CompositeEventTimeline
              events={events}
              mics={scenario.mics}
              durationSec={scenario.durationSec}
              verdictLabel={ruleTitle.main}
              verdictQualifier={ruleTitle.qualifier}
              t={translate}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
